import { Component, ErrorInfo, ReactNode } from 'react'

interface ErrorBoundaryProps {
    children: ReactNode
    fallback?: ReactNode
}

interface ErrorBoundaryState {
    hasError: boolean
    error: Error | null
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props)
        this.state = {
            hasError: false,
            error: null
        }
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        // Update state so the next render will show the fallback UI
        return {
            hasError: true,
            error: error
        }
    }

    override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // You can log the error to an error reporting service
        console.error('ErrorBoundary caught an error:', error, errorInfo)
    }

    override render(): ReactNode {
        if (this.state.hasError) {
            // You can render any custom fallback UI
            return this.props.fallback || (
                <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-4 text-white">
                    <h2 className="text-2xl mb-4">Something went wrong</h2>
                    <details className="bg-gray-800 p-4 rounded-lg max-w-2xl w-full">
                        <summary className="mb-2 cursor-pointer">Error details</summary>
                        <pre className="text-sm overflow-auto text-red-400 max-h-60">
                            {this.state.error && this.state.error.toString()}
                        </pre>
                    </details>
                    <button
                        onClick={() => this.setState({ hasError: false, error: null })}
                        className="mt-4 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
                    >
                        Try again
                    </button>
                </div>
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary
